/* eslint-disable no-plusplus, prefer-const */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { Card, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import { Helmet } from 'react-helmet-async';

import { ImageGallery } from './components';
import { IAppState } from '../../../store/rootDuck';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import { actions as productActions } from '../../../store/ducks/product.duck';
import Preloader from '../../../components/ui/Preloader/Preloader';
import { actions as cartTypesActions } from '../../../store/ducks/cart.duck';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import { actions as companiesActions } from '../../../store/ducks/companies.duck';
import { actions as productCatalogActions } from '../../../store/ducks/product-catalog.duck';
import { actions as categoriesActions } from '../../../store/ducks/categories.duck';
import { useStylesProducView } from './hooks/useStylesProductView';
import AlertDialog from '../../../components/ui/Dialog/AlertDialog';
import { useDefineUserRole, useFormatMessage } from '../../../hooks';
import { ILocation } from '../../../interfaces/locations';
import { ITypeParameter } from '../../../interfaces/productType';
import ProductIcons from '../../../components/ui/ProductIcons';
import { toAbsoluteUrl } from '../../../../_base';
import { API_DOMAIN } from '../../../constants';
import { Offer } from './components/ProductOffer/Offer';
import { OffersForBuyer } from './components/ProductOffer/OffersForBuyer';
import { useDeleteProduct } from './hooks/useDeleteProduct';

const ProductView: React.FC<TPropsFromRedux> = ({
  product,
  loading,
  isAuthorized,
  me,
  loadingMe,
  fetch,
  clearProduct,
  fetchMe,
  clearMe,
  pageCompany,
  perPageCompany,
  fetchCompanies,
  setCategoryIdActive,
  fetchCatalogCategories,
  catalogCategories,
  setSearchText,
  clearRequest,
}) => {
  const intl = useIntl();
  const { classes } = useStylesProducView();
  const fm = useFormatMessage();
  const navigate = useNavigate();
  const locationRouter = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER', 'ROLE_BUYER_STAFF']);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { deleteProductFetch, successDeleteProduct } = useDeleteProduct();

  const location = useMemo<ILocation | undefined | null>(() => {
    const localLocation = localStorage.getItem('location');
    const parseLocalLocation = localLocation && JSON.parse(localLocation);
    return parseLocalLocation as ILocation;
  }, []);

  const findPathById = (arr: any[], targetId: number | undefined, path = []): any => {
    if (!arr.length || !targetId) {
      return ['Нет даты'];
    }

    for (const item of arr) {
      const newPath: any = [...path, { name: item.name, id: item.id, children: arr }];

      if (item.id === targetId) {
        return newPath;
      }

      if (item.children.length > 0) {
        const foundPath = findPathById(item.children, targetId, newPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }

    return null;
  };

  setLayoutSubheader({
    breadcrumb: [
      {
        name: fm('PRODUCT.CATALOG.ALL.CATEGORIES'),
      },
      ...(findPathById(catalogCategories, product?.category?.id) || []),
    ].map((element: any) => {
      return {
        title: element.name?.length > 30 ? `${element.name.slice(0, 30)} ... ` : element.name,
        hoverTitle: element.name,
        root: true,
        page:
          element.name === fm('PRODUCT.CATALOG.ALL.CATEGORIES')
            ? 'products/catalog/categories'
            : 'products/catalog',
        id: element.id,
        children: element.children,
        onClickCategory: (id: number) => {
          clearRequest();
          setSearchText('');
          setCategoryIdActive(id);
        },
        click: () => {
          setSearchText('');
          dispatch(setCategoryIdActive(element.id || 0));
        },
        isActive: element.id === product?.category?.id,
      };
    }),
  });
  setLayoutFooter({ show: true });

  useEffect(() => {
    fetch({ id: Number(id) });
    return () => {
      clearProduct();
    };
  }, [id]);
  useEffect(() => {
    fetchMe();
    fetchCompanies({
      page: pageCompany,
      perPage: perPageCompany,
      lng: location?.lng,
      lat: location?.lat,
    });
    locationRouter.state?.searchProduct && setSearchText('');
    return () => {
      clearMe();
    };
  }, []);

  useEffect(() => {
    fetchMe();
    return () => {
      clearMe();
    };
  }, []);

  useEffect(() => {
    fetchCatalogCategories({ with_products: true, only_active: 1 });
  }, [fetchCatalogCategories]);

  useEffect(() => {
    if (successDeleteProduct) {
      navigate(-1);
    }
  }, [successDeleteProduct]);

  const deleteAction = () => {
    if (deleteId) {
      deleteProductFetch(deleteId);
      setDeleteId(null);
    }
  };

  const getTranslateParamValue = useCallback(
    (item: ITypeParameter) => {
      if (product && item.value) {
        if (Array.isArray(item.value)) {
          if (item.parameter_enum_values && item.parameter_enum_values.find(i => i.image)) {
            const values: { title: string; image: string }[] = [];
            item.value.forEach(i => {
              const paramEnum = item.parameter_enum_values?.find(
                paramEnum => paramEnum.value === i
              );
              const image = paramEnum?.image || '';
              if (paramEnum) {
                values.push({
                  title: paramEnum?.value || i,
                  image,
                });
              } else {
                values.push({ title: i, image });
              }
            });
            return values;
          }
          const values: string[] = [];
          item.value.forEach(i => {
            const paramEnum = item.parameter_enum_values?.find(
              paramEnum => paramEnum.value === i
            );
            if (paramEnum) {
              values.push(paramEnum?.value || i);
            } else {
              values.push(i);
            }
          });
          return values;
        }
        const paramEnum = item.parameter_enum_values?.find(
          paramEnum => paramEnum.value === item.value
        );
        return paramEnum?.value || item.value;
      }
      return item.value;
    },
    [product]
  );

  const mainCharacteristics = useMemo(() => {
    if (product) {
      const result: { [key: string]: any } = {
        // [intl.formatMessage({ id: 'PRODUCT.VIEW.QUANTITY' })]: product.stock_info
        //   ?.stock_quantity
        //   ? `${product.stock_info?.stock_quantity} шт.`
        //   : null,
        [intl.formatMessage({ id: 'PRODUCER.NAME' })]:
          product.promelec_params?.producer_name || product.manufacturer?.name || null,
        [intl.formatMessage({ id: 'PACKAGE' })]: product.package || null,
      };

      return result;
    }
    return {};
  }, [product, getTranslateParamValue]);

  const characteristics = useMemo(() => {
    if (product) {
      const result: { [key: string]: any } = {
        [intl.formatMessage({ id: 'PRODUCT.VIEW.CATEGORY' })]:
          product.category?.name || intl.formatMessage({ id: 'COMMON.NO_DATA' }),
        [intl.formatMessage({ id: 'REMARK' })]: product.promelec_params?.remark || null,
        [intl.formatMessage({ id: 'DESCRIPTION.ENG' })]:
          product.promelec_params?.description_eng || null,
        [intl.formatMessage({ id: 'TEMPERATURE' })]:
          product.promelec_params?.temperature || null,
        [intl.formatMessage({ id: 'PACKAGING' })]: product.promelec_params?.packaging || null,
        [intl.formatMessage({ id: 'PACK.QUANT' })]:
          product.promelec_params?.pack_quant || null,
        'Единица товара': product.promelec_params?.price_unit || null,
        'Единица измерения': product.promelec_params?.munit || null,
        'Вес брутто единицы товара, г': product.promelec_params?.weight || null,
        [intl.formatMessage({ id: 'PRODUCT.VIEW.WEIGHT' })]: product.stock_info?.weight
          ? `${product.stock_info?.weight} гр`
          : null,
        [intl.formatMessage({ id: 'PRODUCT.VIEW.LENGTH.WIDTH.HEIGHT' })]:
          product.stock_info?.length || product.stock_info?.width || product.stock_info?.height
            ? `${product.stock_info?.length ? product.stock_info?.length + ' мм / ' : ''}${
                product.stock_info?.width ? product.stock_info?.width + ' мм / ' : ''
              }${product.stock_info?.height ? product.stock_info?.height + ' мм' : ''}`
            : null,
        // [intl.formatMessage({ id: 'PRODUCT.VIEW.DELIVERY_TIME' })]: product.delivery_period
        //   ? `${product.delivery_period} недели`
        //   : null,
      };

      product.parameter_values?.forEach(item => {
        const name = item.parameter_name;
        if (item.parameter_id && name) {
          // запрос не возвращает название параметра
          result[name] = getTranslateParamValue(item);
        }
      });

      return result;
    }
    return {};
  }, [product, getTranslateParamValue]);

  const hasMainCharacteristics = Object.values(mainCharacteristics).some(
    value => value !== null
  );
  const hasCharacteristics = Object.values(characteristics).some(value => value !== null);

  if (loading || loadingMe || !product) return <Preloader />;
  return (
    <>
      <>
        {product.meta_data && (
          <Helmet>
            <title>{product.meta_data?.title || 'Chipscan'}</title>
            {product.meta_data?.description && (
              <meta name='description' content={product.meta_data?.description} />
            )}
          </Helmet>
        )}
      </>
      <AlertDialog
        open={!!deleteId}
        message={intl.formatMessage({ id: 'PRODUCT.DELETE.TEXT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setDeleteId(null);
        }}
        handleAgree={() => deleteAction()}
      />
      <div className={classes.container}>
        <div className={classes.navbar}>
          <div>
            <div className={classes.titleContainer}>
              <div
                className={classes.backContainer}
                onClick={() =>
                  locationRouter.key === 'default' ? navigate('/') : navigate(-1)
                }
              >
                <ArrowBackIcon className={classes.backIcon} />
                <p className={classes.backTitle}>
                  {intl.formatMessage({
                    id:
                      locationRouter.key === 'default'
                        ? 'PRODUCT.CATALOG.TITLE'
                        : 'AUTH.BUTTON.BACK',
                  })}
                </p>
              </div>
            </div>
            <h2 className={classes.title}>{product.name}</h2>
            {product?.sku ? <h3 className={classes.pageSubTitle}>{product.sku}</h3> : null}
          </div>
        </div>
        <Card className={classes.card}>
          <div className={classes.topBlock}>
            {product.photos?.length ? <ImageGallery product={product} /> : null}
            <div className={classes.paramsAndDescriptionWrapper}>
            {(hasCharacteristics || hasMainCharacteristics) && (
              <div className={classes.characteristicsContainer}>
                <h2 className={classes.subTitle}>
                  {intl.formatMessage({ id: 'PRODUCT.VIEW.PARAMETERS' })}
                </h2>
                <div className={classes.containerCharacteristics}>
                  {hasMainCharacteristics && (
                    <>
                      <h2 className={classes.tabCommon}>
                        {intl.formatMessage({ id: 'ORDER.TABS.COMMON' })}
                      </h2>
                      <div className={classes.characteristicsValues}>
                        <div className={classes.keysContainer}>
                          {Object.keys(mainCharacteristics).map(key => (
                            <React.Fragment key={key}>
                              {mainCharacteristics[key] &&
                                mainCharacteristics[key] !== 'null' && (
                                  <>
                                    {typeof mainCharacteristics[key]?.[0] === 'object' ? (
                                      <div
                                        key={key}
                                        className={`${classes.caracteristicsListItem} ${classes.charLeftContainer}`}
                                      >
                                        <div className={classes.containerIcons}>
                                          {mainCharacteristics[key].map((el: any) => (
                                            <div key={el} className={classes.containerIcon}>
                                              <ProductIcons
                                                src={el.image}
                                                nameView
                                                title={el.title}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                        <p className={classes.listValue}>{key}:</p>
                                        <p className={`${classes.listValue}`}>
                                          {mainCharacteristics[key]?.toString()}
                                        </p>
                                      </div>
                                    ) : (
                                      <div
                                        key={key}
                                        className={`${classes.caracteristicsListItem} ${classes.charLeftContainer}`}
                                      >
                                        <p className={classes.listValue}>{key}:</p>
                                        <p className={classes.listValueEnd}>
                                          {mainCharacteristics[key]?.toString()}
                                        </p>
                                      </div>
                                    )}
                                  </>
                                )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {hasCharacteristics && (
                    <>
                      <h2 className={classes.tabAdditional}>
                        {intl.formatMessage({ id: 'ORDER.TABS.ADDITIONAL' })}
                      </h2>
                      <div className={classes.characteristicsValues}>
                        <div className={classes.keysContainer}>
                          {Object.keys(characteristics).map(key => (
                            <React.Fragment key={key}>
                              {characteristics[key] && characteristics[key] !== 'null' && (
                                <>
                                  {typeof characteristics[key]?.[0] === 'object' ? (
                                    <div
                                      key={key}
                                      className={`${classes.caracteristicsListItem} ${classes.charLeftContainer}`}
                                    >
                                      <div className={classes.containerIcons}>
                                        {characteristics[key].map((el: any) => (
                                          <div key={el} className={classes.containerIcon}>
                                            <ProductIcons
                                              src={el.image}
                                              nameView
                                              title={el.title}
                                            />
                                          </div>
                                        ))}
                                      </div>
                                      <p className={classes.listValue}>{key}:</p>
                                      <p className={`${classes.listValue}`}>
                                        {characteristics[key]?.toString()}
                                      </p>
                                    </div>
                                  ) : (
                                    <div
                                      key={key}
                                      className={`${classes.caracteristicsListItem} ${classes.charLeftContainer}`}
                                    >
                                      <p className={classes.listValue}>{key}:</p>
                                      <p className={`${classes.listValueEnd}`}>
                                        {characteristics[key]?.toString()}
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            {product.description && (
              <div className={classes.descriptionContainer}>
                <h2 className={classes.subTitle}>
                  {intl.formatMessage({ id: 'PRODUCT.VIEW.DESCRIPTION' })}:
                </h2>
                <p
                  className={classes.description}
                  dangerouslySetInnerHTML={{
                    __html: product.description,
                  }}
                />
              </div>
            )}
          </div>

            {((product.attachments && product.attachments.length > 0) ||
              product?.datasheet) && (
              <div className={classes.characteristicsContainerFile}>
                <h2 className={classes.subTitle}>
                  {intl.formatMessage({ id: 'FILES.TITLE.DATASHEET' })}
                </h2>
                {product?.datasheet ? (
                  <div className={classes.attachment}>
                    <img
                      src={toAbsoluteUrl('/media/icons/link.svg')}
                      alt='link'
                      style={{ width: 20, height: 20 }}
                    />
                    <div>
                      <span className={classes.attachmentTitle}>Ссылка на документацию</span>
                      <div className={classes.containerAttachment}>
                        <span
                          onClick={() => window.open(product.datasheet, '_blank')}
                          className={classes.download}
                        >
                          {intl.formatMessage({ id: 'VIEW' })}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
                {product.attachments && product.attachments.length ? (
                  <div className={classes.containerAttachments}>
                    {product.attachments.map((e, i) => {
                      const splitPath = e.path.split('.');
                      const fileType = splitPath[splitPath.length - 1];
                      let split = e.title.split('.');
                      let fileName = split[0];
                      return (
                        <div key={e.id} className={classes.attachment}>
                          <div>
                            {fileType.toLocaleLowerCase() === 'pdf' ? (
                              <img src={toAbsoluteUrl('/media/icons/pdfFile.svg')} alt='pdf' />
                            ) : (
                              <img
                                src={toAbsoluteUrl('/media/icons/defaultFile.svg')}
                                alt='docx'
                              />
                            )}
                          </div>
                          <div style={{ width: '100%' }}>
                            <span className={classes.attachmentTitle}>{fileName ?? ''}</span>
                            <div className={classes.containerAttachment}>
                              <span className={classes.size}>{e.size}</span>
                              <span
                                onClick={() => window.open(`${API_DOMAIN}${e.path}`, '_blank')}
                                className={classes.download}
                              >
                                {intl.formatMessage({ id: 'BUTTON.DOWNLOAD' })}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            )}
            {/* <div style={{}} /> */}
          </div>

          {id && (
            <>
              {isBuyer || !isAuthorized ? (
                <OffersForBuyer product={product} showMoreItems />
              ) : (
                <Offer id={+id} />
              )}
            </>
          )}
          {me?.is_admin && product.id && (
            <div className={classes.actions}>
              <Button
                variant='contained'
                className={classes.save}
                onClick={() => navigate(`/products/edit/${product.id}`)}
              >
                {fm('edit')}
              </Button>
              <Button
                variant='contained'
                color='error'
                className={classes.save}
                onClick={() => setDeleteId(product.id!)}
              >
                {fm('COMMON.BUTTON.DELETE')}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    product: state.products.product,
    loading: state.products.byIdLoading,
    pageCompany: state.companies.page,
    perPageCompany: state.companies.per_page,
    isAuthorized: state.auth.user != null,
    me: state.profile.me,
    loadingMe: state.profile.loading,
    addProductLoading: state.cart.addProductLoading,
    catalogCategories: state.categories.catalogCategories,
  }),
  {
    fetchCompanies: companiesActions.fetchRequest,
    fetch: productActions.fetchByIdRequest,
    clearProduct: productActions.clearProduct,
    setCategoryIdActive: productCatalogActions.setCategoryIdActive,
    clearRequest: productCatalogActions.clearRequest,
    setSearchText: productCatalogActions.setSearching,
    setProductGuestCart: cartTypesActions.setProductGuestCart,
    fetchMe: profileActions.fetchRequest,
    clearMe: profileActions.clearMe,
    addProductToCart: cartTypesActions.addProductRequest,
    fetchCatalogCategories: categoriesActions.fetchCatalogCategories,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ProductView);
